import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { KID_PICTURES, kidPicture } from "../../constants/kidPins";
import { getKidCards, resetKidPin, unlockKidPin, type KidCardsData } from "../../utils/kidLogin";
import { qrSvgPath } from "../../utils/qrCode";
import { useT, useUiLangStore } from "../../stores/uiLang";

const T = {
  en: {
    title: "Student login cards",
    help: "One card per student: scan the QR code (or open the link) to go straight to the class, then tap your name and your 4 pictures. Print and cut. Keep the PINs private to each child.",
    print: "Print login cards",
    close: "Close",
    loading: "Loading...",
    empty: "No students in this class yet. Import students first.",
    reset: "Reset PIN",
    resetConfirm: (n: string) =>
      `Give ${n} a new picture PIN? Their old card stops working and ${n} is signed out on every device.`,
    resetDone: (n: string) => `New PIN for ${n}. Print a new card.`,
    notSignedOut: "Could not sign out every device. Try Reset PIN again.",
    unlock: "Unlock",
    unlockConfirm: (n: string) => `Unlock ${n}? The same pictures keep working (no new card).`,
    unlockDone: (n: string) => `${n} is unlocked.`,
    wrongWeek: (n: number) => (n === 1 ? "1 wrong try this week" : `${n} wrong tries this week`),
    nearLock: (n: number, max: number) => `${n} of ${max} wrong tries before the card locks`,
    lockHelp:
      "Unlock if the student just mixed up the pictures. Use Reset PIN if you think someone else knows the pictures.",
    scan: "Scan or go to",
    classCode: "Class code",
    go: "Go to",
    pictures: "Your pictures, in order",
    failed: "Could not load login cards.",
    locked: "Locked after too many wrong tries.",
    emailStudents: (n: number) =>
      n === 1
        ? "1 student has an email address and signs in with email and password, so no card is printed."
        : `${n} students have email addresses and sign in with email and password, so no cards are printed.`,
  },
  es: {
    title: "Tarjetas de entrada",
    help: "Una tarjeta por estudiante: escanea el código QR (o abre el enlace) para ir directo a la clase, toca tu nombre y tus 4 dibujos. Imprime y recorta. Cada PIN es privado.",
    print: "Imprimir tarjetas",
    close: "Cerrar",
    loading: "Cargando...",
    empty: "Todavía no hay estudiantes en esta clase. Primero importa estudiantes.",
    reset: "Cambiar PIN",
    resetConfirm: (n: string) =>
      `¿Dar a ${n} un PIN nuevo? Su tarjeta anterior deja de funcionar y se cierra su sesión en todos los dispositivos.`,
    resetDone: (n: string) => `PIN nuevo para ${n}. Imprime una tarjeta nueva.`,
    notSignedOut: "No se pudo cerrar la sesión en todos los dispositivos. Vuelve a usar Cambiar PIN.",
    unlock: "Desbloquear",
    unlockConfirm: (n: string) => `¿Desbloquear a ${n}? Los mismos dibujos siguen funcionando (sin tarjeta nueva).`,
    unlockDone: (n: string) => `${n} está desbloqueado.`,
    wrongWeek: (n: number) => (n === 1 ? "1 intento equivocado esta semana" : `${n} intentos equivocados esta semana`),
    nearLock: (n: number, max: number) => `${n} de ${max} intentos equivocados antes de bloquear la tarjeta`,
    lockHelp:
      "Desbloquea si el estudiante solo confundió los dibujos. Usa Cambiar PIN si crees que otra persona conoce los dibujos.",
    scan: "Escanea o entra en",
    classCode: "Código de clase",
    go: "Entra en",
    pictures: "Tus dibujos, en orden",
    failed: "No se pudieron cargar las tarjetas.",
    locked: "Bloqueado por demasiados intentos.",
    emailStudents: (n: number) =>
      n === 1
        ? "1 estudiante tiene correo electrónico y entra con correo y contraseña, así que no se imprime tarjeta."
        : `${n} estudiantes tienen correo electrónico y entran con correo y contraseña, así que no se imprimen tarjetas.`,
  },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
  className: string;
}

export default function KidLoginCardsModal({ isOpen, onClose, classId }: Props) {
  const t = useT(T);
  const lang = useUiLangStore((s) => s.lang);
  const [data, setData] = useState<KidCardsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    getKidCards(classId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.error || !res.data) setError(res.error || t.failed);
      else setData(res.data);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, classId]);

  async function handleReset(id: string, label: string) {
    if (!window.confirm(t.resetConfirm(label))) return;
    const res = await resetKidPin(classId, id);
    if (res.error || !res.data) {
      toast.error(res.error || t.failed);
      return;
    }
    const newPin = res.data.pin;
    setData((d) =>
      d
        ? { ...d, students: d.students.map((s) => (s.id === id ? { ...s, pin: newPin, locked: false, wrongCount: 0 } : s)) }
        : d
    );
    toast.success(t.resetDone(label));
    if (res.data.signedOut === false) toast.warning(t.notSignedOut);
  }

  async function handleUnlock(id: string, label: string) {
    if (!window.confirm(t.unlockConfirm(label))) return;
    const res = await unlockKidPin(classId, id);
    if (res.error || !res.data) {
      toast.error(res.error || t.failed);
      return;
    }
    setData((d) =>
      d ? { ...d, students: d.students.map((s) => (s.id === id ? { ...s, locked: false, wrongCount: 0 } : s)) } : d
    );
    toast.success(t.unlockDone(label));
  }

  // The card's main entry: a link (and QR of it) with the class code filled in, so kids skip typing the code.
  const loginUrl = data ? `${window.location.origin}/kid-login?code=${encodeURIComponent(data.classCode)}` : "";
  const qr = useMemo(() => {
    if (!loginUrl) return null;
    try {
      return qrSvgPath(loginUrl);
    } catch {
      return null; // URL too long for the small encoder: the printed link still works
    }
  }, [loginUrl]);
  const hardLockAt = data?.hardLockAt ?? 15;

  if (!isOpen) return null;

  // Portal to <body> so print CSS can hide every other top-level node with display:none.
  // (visibility:hidden kept the whole dashboard's height in print: blank pages before and after the cards.)
  return createPortal(
    <div className="kid-cards-root fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-start overflow-y-auto py-6">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          html, body { height: auto !important; min-height: 0 !important; overflow: visible !important; background: #fff !important; }
          body > *:not(.kid-cards-root) { display: none !important; }
          .no-print { display: none !important; }
          .kid-cards-root { position: static !important; display: block !important; inset: auto !important; background: none !important; overflow: visible !important; padding: 0 !important; }
          .kid-cards-panel { max-width: none !important; width: 100% !important; box-shadow: none !important; border-radius: 0 !important; }
          .kid-cards-body { padding: 0 !important; }
          .kid-cards-print { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 0.12in !important; }
          .kid-card { break-inside: avoid; page-break-inside: avoid; padding: 0.1in 0.14in !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .kid-card-name { font-size: 20px !important; line-height: 24px !important; }
          .kid-card-qr { width: 76px !important; height: 76px !important; }
          .kid-card > * + * { margin-top: 4px !important; }
          .kid-pins { flex-wrap: nowrap !important; gap: 6px !important; }
          .kid-pin-box { width: 62px !important; height: 62px !important; flex: 0 0 62px; }
          .kid-pin-box .kid-pin-emoji { font-size: 30px !important; }
          .kid-pin-box .kid-pin-word { font-size: 11px !important; line-height: 14px; }
        }
      `}</style>
      <div className="kid-cards-panel bg-white w-full max-w-4xl rounded-lg shadow-lg">
        <div className="no-print px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">{t.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" aria-label={t.close}>
            ✕
          </button>
        </div>
        <div className="kid-cards-body p-6">
          <p className="no-print text-sm text-gray-600 mb-4">{t.help}</p>
          {loading && <p className="text-sm text-gray-500">{t.loading}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {data && data.students.length === 0 && !data.emailStudentCount && (
            <p className="text-sm text-gray-500">{t.empty}</p>
          )}
          {data && !!data.emailStudentCount && (
            <p className="no-print text-sm text-gray-600 mb-4">{t.emailStudents(data.emailStudentCount)}</p>
          )}
          {data && data.students.length > 0 && (
            <>
              <div className="no-print mb-4 flex gap-3">
                <button onClick={() => window.print()} className="btn btn-small">
                  {t.print}
                </button>
                <button onClick={onClose} className="btn btn-small btn-secondary">
                  {t.close}
                </button>
              </div>
              <div className="kid-cards-print grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.students.map((s) => (
                  <div key={s.id} className="kid-card border-2 border-dashed border-gray-400 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="kid-card-name text-3xl font-bold text-gray-900">{s.label}</p>
                      <div className="no-print flex shrink-0 gap-3">
                        {(s.locked || (s.wrongCount ?? 0) > 0) && (
                          <button onClick={() => handleUnlock(s.id, s.label)} className="text-xs text-indigo-700 underline">
                            {t.unlock}
                          </button>
                        )}
                        <button onClick={() => handleReset(s.id, s.label)} className="text-xs text-indigo-700 underline">
                          {t.reset}
                        </button>
                      </div>
                    </div>
                    {s.locked && <p className="no-print mt-1 text-sm font-medium text-red-700">{t.locked}</p>}
                    {(s.wrongThisWeek ?? 0) > 0 && (
                      <p className={`no-print mt-1 text-sm ${s.locked ? "text-red-700" : "text-amber-700"}`}>
                        {t.wrongWeek(s.wrongThisWeek ?? 0)}
                        {!s.locked && (s.wrongCount ?? 0) > 0 ? ` · ${t.nearLock(s.wrongCount ?? 0, hardLockAt)}` : ""}
                      </p>
                    )}
                    {(s.locked || (s.wrongThisWeek ?? 0) > 0) && (
                      <p className="no-print mt-1 text-xs text-gray-500">{t.lockHelp}</p>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                      {qr && (
                        <svg
                          viewBox={`0 0 ${qr.size} ${qr.size}`}
                          className="kid-card-qr h-24 w-24 shrink-0"
                          shapeRendering="crispEdges"
                          role="img"
                          aria-label={loginUrl}
                        >
                          <rect width={qr.size} height={qr.size} fill="#fff" />
                          <path d={qr.path} fill="#000" />
                        </svg>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs text-gray-600">{qr ? t.scan : t.go}:</p>
                        <p className="text-sm font-semibold text-gray-900 break-all">{loginUrl}</p>
                        <p className="mt-1 text-sm text-gray-700">
                          {t.classCode}: <span className="font-mono text-lg font-bold">{data.classCode}</span>
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm font-medium text-gray-800">{t.pictures}</p>
                    <div className="kid-pins mt-1 flex flex-wrap gap-2">
                      {s.pin.map((id, i) => (
                        <div
                          key={i}
                          className="kid-pin-box flex h-[72px] w-[72px] flex-col items-center justify-center rounded-xl border-2 border-gray-700"
                        >
                          <span className="kid-pin-emoji text-4xl leading-none">{kidPicture(id).emoji}</span>
                          <span className="kid-pin-word text-xs font-semibold">{kidPicture(id)[lang]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="no-print mt-4 text-xs text-gray-500">
                {KID_PICTURES.map((p) => p.emoji).join(" ")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
